import {
  AboutModal,
  AlertList,
  AppLayout,
  alertPath,
  FormBellList,
  formatDay,
  hiddenMenuItems,
  notificationCount,
  recordPath,
  TaskBellList,
  useWhatsNew,
  VersionTag,
  WhatsNewDot,
  WhatsNewModal,
} from '@felix/ui';
import { Menu } from '@mantine/core';
import {
  IconCheck,
  IconInfoCircle,
  IconKey,
  IconLogout,
  IconSettings,
  IconSparkles,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { officeApi } from '../api.js';
import { useBranding } from '../branding.js';
import { sloganLines } from '../branding-form.js';
import { useDashboard } from '../dashboard-data.js';
import { LANGUAGES } from '../i18n.js';
import { PATHS } from '../routing.js';
import { useSession } from '../session.js';

/**
 * Oturum açıkken bütün ekranların ortak düzeni: `packages/ui` kabuğu ve menüsü (ADR 0019).
 * `title` tarayıcı sekmesinin başlığıdır; sayfa başlığı `PageHeader`'da.
 */
export function Shell({ title, children }: { title: string; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const user = session.me?.user;
  const { branding } = useBranding();
  const { summary } = useDashboard();
  const [about, setAbout] = useState(false);
  // Yenilikler (M17, ADR 0064): kendiliğinden açılmaz; görülmemiş not menü öğesinde noktayla
  const whatsNew = useWhatsNew('office');

  useEffect(() => {
    document.title = `${title} · Felixmate`;
  }, [title]);

  const signOut = async () => {
    await officeApi.logout().catch(() => undefined);
    session.setStage(null);
    navigate(PATHS.login, { replace: true });
  };

  return (
    <AppLayout
      scope="web"
      // Görme izni olmayan modüller menüde gizli (ADR 0061 §6; ADR 0067 §4): ofis API'si web
      // uygulamasıyla aynı sürümdedir, liste tamdır
      hiddenMenuItems={hiddenMenuItems(session.me?.viewableModules, { complete: true })}
      path={location.pathname}
      search={location.search}
      onNavigate={(path) => navigate(path)}
      brand={
        branding?.name
          ? { name: branding.name, tagline: branding.tagline ?? '', logo: branding.logo }
          : null
      }
      badge={
        branding?.vesselSlogan
          ? sloganLines(branding.vesselSlogan)
          : { title: t('shell.sloganTitle'), subtitle: t('shell.sloganSubtitle') }
      }
      version={(compact) => (
        <VersionTag productName="Felixmate" version={__APP_VERSION__} compact={compact} />
      )}
      // Barda rol satırı; e-posta yalnız kullanıcı menüsünün başlığında (M6f G1-20). Oturum yanıtı
      // henüz rol taşımıyor: ofis kullanıcısı olduğu yazılır
      user={{
        name: user?.fullName ?? user?.email ?? '',
        detail: t('shell.officeUser'),
        email: user?.email,
      }}
      notifications={{
        // Bana düşen işler ve uyarılar tek çağrıdan (ADR 0156): sayı `notificationCount`, liste
        // panodaki grupların aynısı
        count: notificationCount(summary),
        content: (
          <>
            {summary?.tasks ? (
              <TaskBellList tasks={summary.tasks} />
            ) : (
              // İş alanı yoksa (iş doğuran izin yok) ISM form adımları eskisi gibi
              <FormBellList
                items={summary?.approvals?.items ?? []}
                onOpen={(recordId) => navigate(recordPath(recordId))}
              />
            )}
            <AlertList
              alerts={(summary?.alerts ?? []).filter((alert) => alert.kind !== 'missing')}
              limit={6}
              formatDate={formatDay}
              onOpen={(alert) => navigate(alertPath(alert))}
            />
          </>
        ),
      }}
      userMenu={
        <>
          <Menu.Label>{t('shell.language')}</Menu.Label>
          {LANGUAGES.map((language) => (
            <Menu.Item
              key={language}
              onClick={() => void i18n.changeLanguage(language)}
              rightSection={
                i18n.resolvedLanguage === language ? <IconCheck size={14} aria-hidden /> : null
              }
            >
              {t(`language.name.${language}`)}
            </Menu.Item>
          ))}
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconSettings size={16} />}
            onClick={() => navigate(PATHS.settings)}
          >
            {t('menu.settings')}
          </Menu.Item>
          {/* Kendi parolasını değiştirme (ortak 2026-10-30) */}
          <Menu.Item leftSection={<IconKey size={16} />} onClick={() => navigate(PATHS.password)}>
            {t('settingsScreen.account.changePassword')}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconSparkles size={16} />}
            rightSection={whatsNew.unseen ? <WhatsNewDot /> : null}
            onClick={whatsNew.open}
          >
            {t('settingsScreen.whatsNew.open')}
          </Menu.Item>
          {/* About (M9a) [NR467 Pt C Ch 3 Sec 3 7.1.1] */}
          <Menu.Item leftSection={<IconInfoCircle size={16} />} onClick={() => setAbout(true)}>
            {t('settingsScreen.about.open')}
          </Menu.Item>
          <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => void signOut()}>
            {t('shell.signOut')}
          </Menu.Item>
        </>
      }
    >
      {children}
      <AboutModal
        opened={about}
        onClose={() => setAbout(false)}
        version={__APP_VERSION__}
        build={__APP_BUILD__}
      />
      <WhatsNewModal
        opened={whatsNew.opened}
        onClose={whatsNew.close}
        notes={whatsNew.notes}
        unseenIds={whatsNew.unseenIds}
      />
    </AppLayout>
  );
}
